// Everything the CSV preview DECIDES, kept out of the component that paints it.
//
// The split is the one delivery.js and sort-preference.js already use on this screen,
// and for the same reason: there is no component-test harness in this repository, so a
// decision left inside JSX is a decision nothing can check. What is here is the wording,
// the sort, and the two numbers that have to be told the truth; what is left in
// TabularPreview.jsx is elements and classes.
//
// WHAT "TOLD THE TRUTH" MEANS HERE, because it is the whole point of the module.
// The server returns at most 1,000 rows and 100 columns of a file that may have far more
// of each, and reports the real totals in `meta` (docs/superpowers/specs/
// 2026-10-07-tabular-preview-design.md). A table rendered from that payload without a
// notice is indistinguishable from a table of the whole file: an agent searches it, does
// not find the store they wanted, and concludes it is not in the list. Showing 1,000 of
// 50,000 rows silently is worse than refusing to preview at all — so every bound the
// server applied gets a sentence here, and the sort, which runs only over the rows
// already in hand, says so when those rows are not all of them.

/**
 * The extensions this UI will offer a preview button for.
 *
 * A SECOND COPY OF `PREVIEWABLE_EXTENSIONS` IN src/attachments/tabular.js, deliberately
 * and not by accident. The browser bundle must not import server modules, so the rule
 * exists twice; test/web/tabular-preview.test.js imports both and compares them, which
 * is the only thing that can keep this list honest. A button this file offers for an
 * extension the server does not accept is a button whose only outcome is a 415.
 */
export const PREVIEWABLE_EXTENSIONS = Object.freeze(['csv', 'tsv', 'txt']);

/**
 * Is this filename worth asking the preview endpoint about?
 *
 * The extension and nothing else, mirroring the server's `canAttemptPreview` down to the
 * handling of a name with no dot and a name ending in one. It is not a judgement about
 * the bytes and must never be read as one — the server parses them and answers 415 if
 * they are not a table, which is what makes trusting a sender-chosen extension sound
 * here. It chooses which request to make; it never reaches a header or a renderer.
 */
export function isPreviewableFilename(filename) {
  if (typeof filename !== 'string') return false;
  const trimmed = filename.trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot === -1 || dot === trimmed.length - 1) return false;
  return PREVIEWABLE_EXTENSIONS.includes(trimmed.slice(dot + 1).toLowerCase());
}

/**
 * The preview path AS api/client.js wants it — under `/api`, which that module prefixes.
 *
 * `previewHref` in format.js builds the browser-facing URL out of this one, and the test
 * asserts the two are the same path, so the fetch and any link can never address
 * different routes. No `is_downloadable` check lives here: that gate is `previewHref`'s,
 * because it is the one deciding whether a button exists.
 *
 * Both ids are our own integers on the way out and are still encoded, for the same
 * reason `attachmentHref` encodes them: "these are always numbers" is precisely the
 * assumption that stops being true.
 */
export function previewApiPath(publicRef, attachmentId) {
  return `/tickets/${encodeURIComponent(publicRef)}/attachments/${encodeURIComponent(attachmentId)}/preview`;
}

/**
 * Thousands separators, for counts inside fixed English sentences.
 *
 * Grouping is done here rather than with `toLocaleString()` — which is what the customer
 * list's "Showing 1–25 of 300" uses — because these numbers sit inside a sentence that is
 * English in every locale. A number formatted for a French browser inside an English
 * sentence is a mixture, and it would also make the exact wording untestable.
 */
export function formatCount(value) {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** A `meta` count that can actually be used in a sentence, or null. */
function usableCount(value) {
  const count = Number(value);
  return Number.isInteger(count) && count >= 0 ? count : null;
}

/**
 * "Showing 1,000 of 1,500 rows — download the file to read the rest.", or null.
 *
 * THE SENTENCE THE ROW CAP EXISTS TO PAY FOR. Two independent signals are honoured and
 * either one is enough: `meta.truncated`, which the parser sets when it stopped early,
 * and `total_rows` exceeding the rows actually in hand. A stale or missing count cannot
 * silence the flag — it only costs the sentence its numbers.
 *
 * The reverse case is refused: a `total_rows` SMALLER than the rows received means the
 * server contradicted itself, and "Showing 1,000 of 3 rows" is worse than no sentence.
 */
export function truncationNotice(meta, rowCount) {
  const total = usableCount(meta?.total_rows);
  const flagged = meta?.truncated === true;
  if (total !== null && total > rowCount) {
    return `Showing ${formatCount(rowCount)} of ${formatCount(total)} rows — download the file to read the rest.`;
  }
  if (flagged) return 'Some rows are not shown — download the file to read all of them.';
  return null;
}

/**
 * The same sentence for width. A 240-column export previewed at 100 columns has had
 * 140 columns removed from the right-hand side, and nothing on screen would otherwise
 * show that the table simply stops.
 */
export function columnNotice(meta, columnCount) {
  const total = usableCount(meta?.total_columns);
  if (total === null || total <= columnCount) return null;
  return `Showing ${formatCount(columnCount)} of ${formatCount(total)} columns — download the file to see the rest.`;
}

/**
 * The third bound, footnoted rather than headlined: individual cells cut at the
 * cell-length cap. It is a different kind of loss from the row and column caps — the
 * row is there and one value in it ends early — so it reads as a footnote.
 */
export function cellNotice(meta) {
  const cut = usableCount(meta?.truncated_cells);
  if (cut === null || cut === 0) return null;
  return cut === 1
    ? '1 cell was too long to show in full and ends with an ellipsis.'
    : `${formatCount(cut)} cells were too long to show in full and end with an ellipsis.`;
}

/**
 * What a sort means on a truncated preview — shown only once a sort is applied.
 *
 * The sort runs over the rows already sent and never refetches (that is the design's
 * choice, not an omission). On a whole file that is the same answer the server would
 * give. On a truncated one it emphatically is not: sorting the first 1,000 rows of
 * 50,000 by amount gives the largest of THOSE 1,000, which looks exactly like the
 * largest in the file. Hence the sentence, and hence its naming the row count.
 *
 * Before a sort is applied there is nothing to warn about — `truncationNotice` is
 * already saying the table is partial — so this stays null.
 */
export function sortScopeNotice(meta, rowCount, sort) {
  if (!sort?.direction) return null;
  const total = usableCount(meta?.total_rows);
  const partial = meta?.truncated === true || (total !== null && total > rowCount);
  if (!partial) return null;
  return `Sorted within the ${formatCount(rowCount)} rows shown, not the whole file.`;
}

/** A cell that parses as a finite number, or null — the test both sorting and alignment use. */
function numericValue(cell) {
  const text = String(cell ?? '').trim();
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

// `sensitivity: 'base'` so a customer list sorted by name does not put every lowercase
// surname after every uppercase one, which is what a byte comparison does. `numeric` is
// belt and braces for the mixed column — a column of pure numbers is already compared as
// numbers below — and gives "Store 2" before "Store 10".
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * The rows in the order a sorted column asks for. A COPY — never the array it was given.
 *
 * `Array.prototype.sort` sorts in place, and the payload's own order is what the table
 * returns to when the sort is cleared, so mutating here would make that third click
 * impossible. The sort is stable (guaranteed by the language since ES2019), which is
 * what makes a second sort a tie-break on the first rather than a reshuffle.
 *
 * Blanks go last in both directions. An empty cell is absence, not a value smaller than
 * every other value, and burying the populated rows under 400 blank ones is not what
 * anybody clicking a header wanted.
 */
export function sortRows(rows, sort) {
  const column = sort?.column;
  const direction = sort?.direction;
  if (!direction || !Number.isInteger(column) || column < 0) return rows;
  if (!rows.some((row) => column < row.length)) return rows;

  const sign = direction === 'desc' ? -1 : 1;
  return [...rows].sort((left, right) => {
    const a = String(left[column] ?? '');
    const b = String(right[column] ?? '');
    if (a === '' || b === '') {
      if (a === b) return 0;
      return a === '' ? 1 : -1;
    }
    const na = numericValue(a);
    const nb = numericValue(b);
    if (na !== null && nb !== null) return sign * (na - nb);
    return sign * COLLATOR.compare(a, b);
  });
}

/**
 * The next sort state for a click on a header: ascending, descending, then file order.
 *
 * The third state is not decoration. An export usually arrives in an order that means
 * something — newest first, or the order the stores were visited — and an agent who
 * sorted it needs that back without reloading the ticket.
 */
export function nextSort(sort, column) {
  if (sort?.column !== column) return { column, direction: 'asc' };
  if (sort.direction === 'asc') return { column, direction: 'desc' };
  return { column: null, direction: null };
}

/** The `aria-sort` value for one header cell: only the sorted column reports a direction. */
export function ariaSort(sort, column) {
  if (sort?.column !== column || !sort?.direction) return 'none';
  return sort.direction === 'desc' ? 'descending' : 'ascending';
}

/**
 * Which columns hold nothing but numbers, so they can be right-aligned and set in
 * tabular figures. A column is numeric only if at least one cell is a number and no
 * non-empty cell is anything else — one store code with a letter in it makes the whole
 * column text, which is the conservative direction: a misaligned number is ugly, a
 * right-aligned ragged column of words is unreadable.
 */
export function numericColumnFlags(rows, columnCount) {
  const flags = new Array(columnCount).fill(false);
  for (let column = 0; column < columnCount; column += 1) {
    let sawNumber = false;
    let allNumeric = true;
    for (const row of rows) {
      const cell = String(row[column] ?? '').trim();
      if (cell === '') continue;
      if (numericValue(cell) === null) { allNumeric = false; break; }
      sawNumber = true;
    }
    flags[column] = allNumeric && sawNumber;
  }
  return flags;
}

/**
 * The header to show for one column. A blank header is named by position rather than
 * rendered as an empty cell: the column is still there, still full of values, and still
 * has to be a thing an agent can point at and sort by.
 */
export function columnLabel(columns, index) {
  const name = typeof columns?.[index] === 'string' ? columns[index].trim() : '';
  return name === '' ? `Column ${index + 1}` : name;
}

const DELIMITER_LABELS = Object.freeze({
  ',': 'Comma-separated',
  ';': 'Semicolon-separated',
  '\t': 'Tab-separated',
  '|': 'Pipe-separated',
});

/**
 * What the parser decided the file was, in words, or null for anything this UI has no
 * name for. Worth showing because the failure it catches is silent otherwise: a
 * semicolon file read as commas is a perfectly valid one-column table, and the only way
 * an agent can tell that from a genuinely one-column file is being told which delimiter
 * was used.
 */
export function delimiterLabel(delimiter) {
  return DELIMITER_LABELS[delimiter] ?? null;
}

// The reasons the route can send with a 415 (src/routes/api/tickets.js §3.2c). The route
// sends a code rather than the parser's own message precisely so that nothing derived
// from the file's contents reaches a response, so the sentence has to be written here.
const REFUSAL_DETAIL = Object.freeze({
  TOO_LARGE: 'It is too large to preview.',
  NOT_TEXT: 'Its contents are not text.',
  ENCODING: 'Its text could not be decoded.',
  NOT_PREVIEWABLE: 'It is not a kind of file this can read.',
});

/**
 * What to show instead of a table, given the error `api/client.js` threw.
 *
 * A 415 is the file, not the request: the download link beside this message is still
 * live and is still the answer, which is why every branch here is phrased as a fact
 * about the file rather than as an apology.
 *
 * A 404 must not guess. The loader answers one indistinguishable 404 for purged,
 * not-yet-stored, wrong-ticket and no-such-ticket, deliberately, so that the route
 * cannot be used to find out which attachment ids exist. Naming a cause here would be
 * inventing one.
 */
export function previewErrorMessage(error) {
  if (error?.status === 415) {
    const detail = REFUSAL_DETAIL[error?.details?.reason];
    const base = 'This file could not be read as a table.';
    return detail ? `${base} ${detail}` : base;
  }
  if (error?.status === 404) return 'This attachment is no longer available.';
  return 'This preview could not be loaded.';
}
